import { useState } from "react";
import { GetStaticProps, InferGetStaticPropsType } from "next";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  Warning,
  XCircle,
  Info,
  ArrowRight,
  ShieldCheck,
  CaretDown,
  CaretUp,
  GraduationCap,
  ArrowCounterClockwise,
  Lightning,
} from "@phosphor-icons/react";
import Link from "next/link";
import { defaultLayout } from "./layout/default";
import SEO from "lib/components/SEO";
import { breadcrumbLd, faqLd, organizationLd, howToLd } from "lib/seo/jsonLd";
import { host } from "utils/constants";

// ── Types ─────────────────────────────────────────────────────────────────────

interface CampusProgramOption {
  program: string;
  feeType: string;
}

interface CampusOption {
  key: string;
  name: string;
  programs: CampusProgramOption[];
}

interface Options {
  campuses: CampusOption[];
  feeTypes: string[];
  quotas: string[];
}

type Verdict = "likely" | "possible" | "unlikely" | "no_data";

interface PredictResult {
  verdict: Verdict;
  message: string;
  campus?: string;
  program?: string;
  feeType?: string;
  quota?: string;
  rank?: number;
  seatTarget?: number;
}

// ── Static Props ───────────────────────────────────────────────────────────────

export const getStaticProps: GetStaticProps<{ options: Options | null }> = async () => {
  try {
    const res = await axios.get(`${host}/eu/rank-predictor/options`);
    const data = res.data?.data ?? res.data ?? null;
    return { props: { options: data }, revalidate: 3600 };
  } catch {
    return { props: { options: null }, revalidate: 60 };
  }
};

// ── Verdict config ─────────────────────────────────────────────────────────────

const VERDICT_CONFIG: Record<
  Verdict,
  { label: string; sublabel: string; color: string; bg: string; border: string; Icon: React.ElementType }
> = {
  likely: {
    label: "Safe — High Chance",
    sublabel: "Your rank is within the first-round cutoff based on historical data.",
    color: "text-green-700",
    bg: "bg-green-50",
    border: "border-green-200",
    Icon: CheckCircle,
  },
  possible: {
    label: "Borderline — Apply & Monitor",
    sublabel: "You have qualified historically, but usually in a later admission round. Apply and watch the notice board.",
    color: "text-amber-700",
    bg: "bg-amber-50",
    border: "border-amber-200",
    Icon: Warning,
  },
  unlikely: {
    label: "Tough — Low Chance",
    sublabel: "Your rank has not cleared this cutoff in past records. Consider alternative campuses or programs.",
    color: "text-red-700",
    bg: "bg-red-50",
    border: "border-red-200",
    Icon: XCircle,
  },
  no_data: {
    label: "No Data Available",
    sublabel: "We do not have cutoff records for this combination yet.",
    color: "text-gray-600",
    bg: "bg-gray-50",
    border: "border-gray-200",
    Icon: Info,
  },
};

// ── FAQ ────────────────────────────────────────────────────────────────────────

const FAQ_ITEMS = [
  {
    question: "What IOE rank do I need for Computer Engineering at Pulchowk Campus?",
    answer:
      "For Computer Engineering at Pulchowk Campus (full-fee), you typically need a rank within the top 150–210. The scholarship (regular) cutoff is around rank 27–35. Exact cutoffs move slightly each year based on total applicants — always confirm with the official IOE notice board after results are declared.",
  },
  {
    question: "What are the IOE college cutoff ranks for 2082?",
    answer:
      "Key IOE 2082 cutoff ranks (first list, open/general): Pulchowk Computer scholarship ~rank 30, Pulchowk Computer full-fee ~rank 207, Pulchowk Civil full-fee ~rank 350, Thapathali Computer full-fee ~rank 620, WRC Computer full-fee ~rank 1,200+. Select your campus and program in the predictor above for a specific estimate.",
  },
  {
    question: "How accurate is the IOE rank predictor?",
    answer:
      "The predictor uses verified first-list and second-list cutoff data from multiple IOE exam cycles. Because the exact cutoff shifts slightly year to year, treat the result as a strong directional signal rather than a guarantee. 'Safe' means you have cleared the first-round cutoff in every year on record. 'Borderline' means you qualified in a later round at least once.",
  },
  {
    question: "What is the difference between Scholarship (Regular) and Full Fee at IOE?",
    answer:
      "Scholarship (Regular) seats are government-funded, have lower annual fees, and carry tighter rank cutoffs. Full-fee seats cost more per year but accept a wider rank range. Both streams award the exact same BE degree from Tribhuvan University and carry equal value with employers.",
  },
  {
    question: "How does the IOE quota system work for 2083 admissions?",
    answer:
      "IOE reserves a share of seats under two quotas: Open (Regular), which is purely merit-based and open to all candidates, and Female, reserved exclusively for female candidates with a slightly more generous rank cutoff. Select the quota that officially applies to you in the predictor for the most accurate result.",
  },
  {
    question: "What happens if the predictor says 'Borderline'? Can I still get in?",
    answer:
      "Yes. IOE releases multiple merit lists — typically three or more — over several weeks after the first list. A 'Borderline' result means you have historically cleared the cutoff in round two or three, as higher-ranked students take admission elsewhere. Monitor the official IOE notice board and Clamphook announcements throughout the admission window.",
  },
  {
    question: "Which IOE campus should I target with a rank between 300 and 700?",
    answer:
      "With a rank of 300–700, you are competitive for: Pulchowk Civil and Electrical (full fee), Thapathali Computer and Civil, Paschimanchal (WRC) Computer and Civil, and Purwanchal (ERC) programs. Use the predictor for each option, then apply to your top two or three choices before the deadline.",
  },
  {
    question: "Is this the same as the official IOE admission system?",
    answer:
      "No. This is an independent rank predictor built by Clamphook Academy using publicly available IOE admission data. It is not affiliated with the Institute of Engineering (IOE) or Tribhuvan University. The official admission process, merit lists, and cutoffs are published by IOE on their official notice board.",
  },
];

// ── Accordion ──────────────────────────────────────────────────────────────────

function AccordionItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        className="w-full flex items-start justify-between gap-4 py-5 text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="font-semibold text-gray-900 text-sm md:text-base leading-snug">
          {question}
        </span>
        {open ? (
          <CaretUp size={16} className="flex-shrink-0 text-primary mt-1" weight="bold" />
        ) : (
          <CaretDown size={16} className="flex-shrink-0 text-gray-400 mt-1" weight="bold" />
        )}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-sm text-gray-600 leading-relaxed">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

const EMPTY_FORM = { name: "", phone: "", rank: "", campus: "", program: "", feeType: "", quota: "" };

function IOERankPredictor({ options }: InferGetStaticPropsType<typeof getStaticProps>) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [result, setResult] = useState<PredictResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedCampusObj = options?.campuses.find((c) => c.key === form.campus) ?? null;

  const programNames = [
    ...new Set((selectedCampusObj?.programs ?? []).map((p) => p.program)),
  ];

  const feeTypes = [
    ...new Set(
      (selectedCampusObj?.programs ?? [])
        .filter((p) => p.program === form.program)
        .map((p) => p.feeType),
    ),
  ];

  const quotas: string[] = options?.quotas ?? ["open", "female"];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "campus") { next.program = ""; next.feeType = ""; }
      if (name === "program") { next.feeType = ""; }
      return next;
    });
    setResult(null);
    setError(null);
  };

  const handleClear = () => { setForm(EMPTY_FORM); setResult(null); setError(null); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const res = await axios.post(`${host}/eu/rank-predictor/predict`, {
        name: form.name,
        phone: form.phone,
        rank: Number(form.rank),
        program: form.program,
        feeType: form.feeType,
        campus: form.campus,
        quota: form.quota,
      });
      setResult(res.data?.data ?? res.data);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const verdictCfg = result ? VERDICT_CONFIG[result.verdict] : null;

  const jsonLd = [
    organizationLd(),
    breadcrumbLd([
      { name: "Home", url: "/" },
      { name: "IOE Rank Predictor 2083", url: "/ioe-rank-predictor" },
    ]),
    {
      "@type": "WebApplication",
      name: "Free IOE Rank Predictor 2083 | College Cutoffs 2082",
      url: "https://clamphook.com/ioe-rank-predictor",
      description:
        "Free IOE entrance rank predictor for 2083. Check your admission chances at Pulchowk, Thapathali, WRC, ERC, and Chitwan using verified IOE college cutoff data from 2082. Built by Clamphook Academy — Nepal's #1 IOE prep platform.",
      applicationCategory: "EducationalApplication",
      operatingSystem: "Any",
      offers: { "@type": "Offer", price: "0", priceCurrency: "NPR" },
      provider: {
        "@type": "EducationalOrganization",
        name: "Clamphook Academy",
        url: "https://clamphook.com",
      },
      inLanguage: "en-NP",
      audience: { "@type": "EducationalAudience", educationalRole: "student" },
    },
    howToLd({
      name: "How to use the IOE Rank Predictor 2083",
      description:
        "Check your IOE college admission chances in under 2 minutes using Clamphook's rank predictor tool.",
      totalTime: "PT2M",
      steps: [
        { name: "Enter your IOE entrance rank", text: "Type the rank from your official IOE entrance result." },
        { name: "Select your target campus", text: "Choose the campus — Pulchowk, Thapathali, WRC, ERC, or Chitwan." },
        { name: "Pick your program, fee type, and quota", text: "Select the BE program, scholarship or full-fee stream, and your applicable quota." },
        { name: "Get your instant prediction", text: "Click Predict My Result to see Safe, Borderline, or Tough — based on verified IOE 2082 cutoff data." },
      ],
    }),
    faqLd(FAQ_ITEMS),
  ];

  return (
    <>
      <SEO
        title="Free IOE Rank Predictor 2083 | College Cutoffs 2082 — Clamphook"
        description="Free IOE rank predictor 2083. Check your exact admission chances at Pulchowk, Thapathali, WRC, ERC, and Chitwan using verified IOE college cutoff data from 2082. Safe, Borderline, or Tough — know before you apply."
        canonicalUrl="https://clamphook.com/ioe-rank-predictor"
        ogImage="/img/Clamphook.png"
        ogType="website"
        keywords={[
          "IOE rank predictor 2083",
          "IOE college cutoff 2082",
          "Pulchowk rank predictor",
          "IOE entrance rank predictor Nepal",
          "IOE cutoff rank 2082",
          "IOE college predictor Nepal",
          "Pulchowk Computer Engineering cutoff rank",
          "IOE rank to college Nepal",
          "IOE college cutoff checker",
          "IOE 2083 rank predictor free",
        ]}
        jsonLd={jsonLd}
      />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="bg-primary-900 pt-16 pb-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="max-w-2xl">
            <p className="text-primary-300 text-sm font-semibold mb-4 tracking-wide">
              Free · Verified IOE Cutoff Data · 2083
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-[1.08] tracking-tight mb-6">
              IOE Rank Predictor 2083{" "}
              <span className="text-primary-300">&amp; College Cutoff Checker</span>
            </h1>
            <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-xl">
              Enter your IOE entrance rank and see instantly whether your seat is{" "}
              <strong className="text-white font-semibold">Safe</strong>,{" "}
              <strong className="text-white font-semibold">Borderline</strong>, or{" "}
              <strong className="text-white font-semibold">Tough</strong> — verified against
              IOE 2082 first-list cutoff data across all campuses.
            </p>
            <div className="flex items-center gap-6 text-white/50 text-sm">
              <span>
                <strong className="text-white font-bold text-lg">100,000+</strong>{" "}
                students prepared
              </span>
              <span className="w-px h-4 bg-white/20" />
              <span>All IOE campuses</span>
              <span className="w-px h-4 bg-white/20" />
              <span>No sign-up needed</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Tool ─────────────────────────────────────────────────────────── */}
      <section className="bg-gray-50 py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto grid md:grid-cols-[1fr_1.3fr] gap-10 items-start">

          {/* Left — editorial context */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-3">How the predictor works</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              We compare your rank against historical program-level IOE cutoff data — first,
              second, and third merit lists — from the past two exam cycles across every
              constituent campus. Select your quota (Open or Female) and fee type to get the
              most accurate picture.
            </p>

            <div className="space-y-2 mb-8">
              {(["likely", "possible", "unlikely"] as Verdict[]).map((key) => {
                const cfg = VERDICT_CONFIG[key];
                const Icon = cfg.Icon;
                return (
                  <div key={key} className="flex items-start gap-3 py-2">
                    <Icon size={16} weight="fill" className={`${cfg.color} flex-shrink-0 mt-0.5`} />
                    <p className="text-sm text-gray-700 leading-relaxed">
                      <span className={`font-bold ${cfg.color}`}>{cfg.label.split(" — ")[0]}</span>
                      {" — "}
                      <span className="text-gray-500">{cfg.sublabel}</span>
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Data note */}
            <div className="flex gap-3 mb-8">
              <ShieldCheck size={15} className="text-primary flex-shrink-0 mt-0.5" weight="fill" />
              <p className="text-xs text-gray-500 leading-relaxed">
                Estimates use historical IOE open/general first-list data and are indicative only.
                Official IOE merit lists supersede all predictions. Not affiliated with IOE or TU.
              </p>
            </div>

            {/* YouTube embed */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                Watch before you apply
              </p>
              <div className="rounded-2xl overflow-hidden border border-gray-200 aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/akFg8_aqel4"
                  title="IOE Quota Seats 2083 Full Information 🔥 कुन कोटामा APPLY गर्ने?"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2 leading-snug">
                IOE Quota Seats 2083 Full Information — कुन कोटामा APPLY गर्ने?
              </p>
            </div>
          </div>

          {/* Right — form */}
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">

            {/* Form header */}
            <div className="bg-primary px-6 py-5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                <GraduationCap size={18} className="text-white" weight="fill" />
              </div>
              <div>
                <h2 className="text-white font-bold text-base leading-tight">
                  Prediction Criteria
                </h2>
                <p className="text-primary-200 text-xs mt-0.5">
                  Free · Instant · No account needed
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">

              {/* Name + Phone */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    placeholder="e.g. 98XXXXXXXX"
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                  />
                </div>
              </div>

              {/* Rank */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-gray-700">
                    IOE Entrance Rank
                  </label>
                  <span className="text-[10px] font-bold text-primary uppercase tracking-wide">
                    * Required
                  </span>
                </div>
                <input
                  type="number"
                  name="rank"
                  value={form.rank}
                  onChange={handleChange}
                  required
                  min={1}
                  placeholder="e.g. 450"
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                />
              </div>

              {/* Campus + Quota */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Target Campus
                  </label>
                  <select
                    name="campus"
                    value={form.campus}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition appearance-none"
                  >
                    <option value="">Select Campus</option>
                    {(options?.campuses ?? []).map((c) => (
                      <option key={c.key} value={c.key}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Quota / Category
                  </label>
                  <select
                    name="quota"
                    value={form.quota}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition appearance-none"
                  >
                    <option value="">Select Quota</option>
                    {quotas.map((q) => (
                      <option key={q} value={q}>
                        {q === "open" ? "Regular (Open Merit)" : q === "female" ? "Female" : q.charAt(0).toUpperCase() + q.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Program + Fee Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Preferred Program
                  </label>
                  <select
                    name="program"
                    value={form.program}
                    onChange={handleChange}
                    required
                    disabled={!form.campus}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition appearance-none disabled:opacity-50"
                  >
                    <option value="">Select Program</option>
                    {programNames.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Program Type
                  </label>
                  <select
                    name="feeType"
                    value={form.feeType}
                    onChange={handleChange}
                    required
                    disabled={!form.program}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition appearance-none disabled:opacity-50"
                  >
                    <option value="">Select Type</option>
                    {feeTypes.map((f) => (
                      <option key={f} value={f}>
                        {f === "regular" ? "Scholarship (Regular)" : f === "fullFee" ? "Full Fee" : f}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={handleClear}
                  className="flex items-center gap-1.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 font-semibold text-sm px-4 py-2.5 transition-colors whitespace-nowrap"
                >
                  <ArrowCounterClockwise size={14} weight="bold" />
                  Clear
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 bg-primary-900 hover:bg-primary-800 disabled:opacity-60 text-white font-semibold rounded-xl py-2.5 px-5 text-sm transition-colors"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Predicting…
                    </>
                  ) : (
                    <>
                      <Lightning size={14} weight="fill" />
                      Predict My Result
                    </>
                  )}
                </button>
              </div>

              {error && <p className="text-sm text-red-600 text-center pt-1">{error}</p>}

              {/* Result */}
              <AnimatePresence>
                {result && verdictCfg && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className={`rounded-2xl border ${verdictCfg.border} ${verdictCfg.bg} p-5`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <verdictCfg.Icon size={22} weight="fill" className={verdictCfg.color} />
                      <p className={`font-bold text-base ${verdictCfg.color}`}>
                        {verdictCfg.label}
                      </p>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {result.message}
                    </p>
                    <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                      {verdictCfg.sublabel}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </div>
        </div>

        {/* Disclaimer strip */}
        <p className="max-w-5xl mx-auto mt-8 text-xs text-center text-gray-400 leading-relaxed px-2">
          This tool uses historical IOE open/general first-list data and is for
          educational estimation only. It is not affiliated with the Institute of
          Engineering (IOE) or Tribhuvan University. Official merit lists supersede
          all predictions.
        </p>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <section className="bg-primary-900 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <h2 className="text-2xl md:text-3xl font-black text-white mb-3">
              Now prepare to earn that rank
            </h2>
            <p className="text-white/70 leading-relaxed max-w-lg">
              Built by Pulchowk engineering graduates. Weekly CBT mock tests identical to the
              real IOE exam, daily doubt sessions, and 30+ mentors who cracked it themselves.
              100,000+ students have prepared with Clamphook.
            </p>
          </div>
          <div className="flex flex-col gap-3 flex-shrink-0">
            <Link
              href="/classrooms"
              className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-600 text-white font-semibold rounded-xl px-7 py-3.5 transition-colors whitespace-nowrap"
            >
              View IOE Courses <ArrowRight size={16} weight="bold" />
            </Link>
            <Link
              href="/ioe-entrance-preparation"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white/80 hover:text-white hover:border-white/60 font-semibold rounded-xl px-7 py-3.5 transition-colors whitespace-nowrap text-sm"
            >
              About IOE Prep
            </Link>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="bg-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto grid md:grid-cols-[1fr_2fr] gap-12 items-start">
          <div>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-4 leading-tight">
              IOE rank, cutoffs &amp; admission — answered
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed">
              Common questions from IOE aspirants about cutoff ranks, quota seats,
              admission rounds, and campus selection.
            </p>
          </div>
          <div className="divide-y divide-gray-200">
            {FAQ_ITEMS.map((faq, i) => (
              <AccordionItem key={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

IOERankPredictor.getLayout = defaultLayout;
export default IOERankPredictor;
